import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/docs/docs/node_modules/gatsby-theme-docz/src/base/Layout.js";
import { BannerAlertBox } from '@entur/alert';
import { ExpandablePanel } from "@entur/expand";
import { Link, ListItem, NumberedList } from '@entur/typography';
import './guides.scss';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "recommendations-guide"
    }}>{`Recommendations Guide`}</h1>
    <p>{`When searching for public transport tickets, the API returns numerous offers for the same journey, varying by flexibility, fare class, accommodation, and other properties. Recommendations group these offers into categories and select the best offer in each category. For journeys with multiple legs, recommendations can also combine offers across legs to cover the entire journey.`}</p>
    <p>{`The API does not guarantee recommendations for every requested category. A category may be empty if, for example, the product does not exist for that departure (e.g., sleeper is only available on night trains), capacity is sold out, or your organisation is not authorised to sell a particular operator's products.`}</p>
    <p>{`Any search endpoint that takes geographical input supports recommendations (e.g., `}<inlineCode parentName="p">{`POST /trip-patterns`}</inlineCode>{`, `}<inlineCode parentName="p">{`POST /stop-places`}</inlineCode>{`, `}<inlineCode parentName="p">{`POST /zones`}</inlineCode>{`). Add a `}<inlineCode parentName="p">{`recommendationConfig`}</inlineCode>{` to your search request to receive recommendations organised by the requested criteria.`}</p>
    <h2 {...{
      "id": "table-of-contents"
    }}>{`Table of Contents`}</h2>
    <NumberedList className="offers-guides-toc" mdxType="NumberedList">
    <ListItem mdxType="ListItem">
        <Link href="#recommended-configuration" mdxType="Link">Recommended Configuration</Link>
    </ListItem>
    <ListItem mdxType="ListItem">
        <Link href="#rulespec" mdxType="Link">RuleSpec</Link>
    </ListItem>
    <ListItem mdxType="ListItem">
        <Link href="#categoryspec" mdxType="Link">CategorySpec</Link>
    </ListItem>
    <ListItem mdxType="ListItem">
        <Link href="#traveller-assignment" mdxType="Link">Traveller Assignment</Link>
    </ListItem>
    <ListItem mdxType="ListItem">
        <Link href="#examples" mdxType="Link">Examples</Link>
    </ListItem>
    <ListItem mdxType="ListItem">
        <Link href="#troubleshooting" mdxType="Link">Troubleshooting</Link>
    </ListItem>
    </NumberedList>
    <br />
    <h2 {...{
      "id": "recommended-configuration"
    }}>{`Recommended Configuration`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <h3 {...{
      "id": "minimal-configuration"
    }}>{`Minimal configuration`}</h3>
    <p>{`To get started, add a `}<inlineCode parentName="p">{`recommendationConfig`}</inlineCode>{` to your search request. Without this config, the API returns offers but no recommendations.`}</p>
    <ExpandablePanel title="Minimal configuration example" mdxType="ExpandablePanel">
      <pre>{`{
  "recommendationConfig": {
    "categorySpec": {
      "typesOfRecommendation": ["CHEAPEST"]
    }
  }
}`}</pre>
    </ExpandablePanel>
    <p>{`This returns the cheapest offer for the entire journey. All other settings use defaults.`}</p>
    <h3 {...{
      "id": "production-configuration"
    }}>{`Production configuration`}</h3>
    <p>{`For production use, enable the recommended flags. This configuration covers most use cases.`}</p>
    <ExpandablePanel title="Production configuration example" mdxType="ExpandablePanel">
      <pre>{`{
  "recommendationConfig": {
    "categorySpec": {
      "typesOfRecommendation": ["CHEAPEST", "NON_FLEXIBLE", "SEMI_FLEXIBLE", "FLEXIBLE"]
    },
    "ruleSpec": {
      "journeyOrganizeAlgorithm": "SUBSEQUENT_COMBINATIONS",
      "priceComparisonAlgorithm": "TOTAL_PRICE",
      "onlyIncludeRecommendationsWithOffersToBuy": true,
      "onlyIncludeRecommendedOffers": true,
      "mixinOffersWithHigherFlexibility": true,
      "mixInOffersWithOtherFacilitySets": true,
      "removeSplitRailReplacementOffers": true
    }
  }
}`}</pre>
    </ExpandablePanel>
    <p>{`These are the recommended flag values for most integrations. See `}<a parentName="p" {...{
        "href": "#categoryspec"
      }}>{`CategorySpec`}</a>{` and `}<a parentName="p" {...{
        "href": "#rulespec"
      }}>{`RuleSpec`}</a>{` for details on each field, and `}<a parentName="p" {...{
        "href": "#examples"
      }}>{`Examples`}</a>{` for behaviour illustrations.`}</p>
    <p>{`If you sell long-distance train tickets, also add `}<inlineCode parentName="p">{`fareClasses`}</inlineCode>{` and `}<inlineCode parentName="p">{`facilitySet`}</inlineCode>{` to the `}<inlineCode parentName="p">{`categorySpec`}</inlineCode>{` to get recommendations split by fare class (e.g., standard vs. premium) and accommodation type (e.g., sleeper, couchette). See `}<a parentName="p" {...{
        "href": "#categoryspec"
      }}>{`CategorySpec`}</a>{` for available values.`}</p>
    <h3 {...{
      "id": "response-structure"
    }}>{`Response structure`}</h3>
    <p>{`The API response contains two related arrays:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li"><inlineCode parentName="strong">{`offers[]`}</inlineCode></strong>{` — All offer objects with full details (price, conditions, validity)`}</li>
      <li parentName="ul"><strong parentName="li"><inlineCode parentName="strong">{`recommendations[]`}</inlineCode></strong>{` — Categories pointing to offers via `}<inlineCode parentName="li">{`offersToBuy`}</inlineCode></li>
    </ul>
    <ExpandablePanel title="Response structure example" mdxType="ExpandablePanel">
      <pre>{`{
  "offers": [
    {
      "id": "offer-1",
      "price": { "amount": "299.00", "currency": "NOK" },
      "geographicalValidity": {
        "serviceJourneys": [{ "serviceJourneyId": "SJ:1" }],
        "pointToPointValidity": { "fromPlace": "NSR:StopPlace:Example1", "toPlace": "NSR:StopPlace:Example2" }
      },
      "preassignedProducts": [{ ..., "properties": { "isExchangeable": true, "isRefundable": true, ..., "fareClasses": ["STANDARD_CLASS"], ... } }],
      ...
    },
    {
      "id": "offer-2",
      "price": { "amount": "399.00", "currency": "NOK" },
      "geographicalValidity": {
        "serviceJourneys": [{ "serviceJourneyId": "SJ:1" }],
        "pointToPointValidity": { "fromPlace": "NSR:StopPlace:Example1", "toPlace": "NSR:StopPlace:Example2" }
      },
      "preassignedProducts": [{ ..., "properties": { "isExchangeable": true, "isRefundable": true, ..., "fareClasses": ["STANDARD_CLASS"], ... } }],
      ...
    }
  ],
  "recommendations": [
    {
      "geographicalValidityCovered": {
        "coversEntireJourney": true,
        "serviceJourneys": ["SJ:1"],
        "pointToPointValidity": {
          "fromPlace": "NSR:StopPlace:Example1",
          "toPlace": "NSR:StopPlace:Example2"
        }
      },
      "typeOfRecommendation": "FLEXIBLE",
      "fareClass": "STANDARD_CLASS",
      "facilitySet": "ANY_FACILITY_SET",
      "offersToBuy": [
        {
          "id": "offer-1",
          "numberToBuy": 1,
          "selectableProductIds": [],
          "possibleTravellerIds": [["traveller-1"]]
        }
      ]
    }
  ]
}`}</pre>
    </ExpandablePanel>
    <p>{`In this example, the recommendation selects offer-1 as the cheapest option in the FLEXIBLE + STANDARD_CLASS category. Both offers cover the same journey with the same flexibility, but differ in price.`}</p>
    <p><strong parentName="p">{`Key fields in a recommendation:`}</strong></p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`typeOfRecommendation`}</inlineCode>{` — The flexibility category (CHEAPEST, NON_FLEXIBLE, etc.)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`geographicalValidityCovered`}</inlineCode>{` — Which part of the journey this covers:`}
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`coversEntireJourney`}</inlineCode>{` — Boolean indicating if this recommendation covers the full journey`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`serviceJourneys`}</inlineCode>{` — List of service journey IDs covered, only applicable when requesting offers for a specific TripPattern`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`pointToPointValidity`}</inlineCode>{` — Start and end stop places, for tickets valid between specific stops`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`zonalValidity`}</inlineCode>{` — Zone information, for tickets valid in specific zones`}</li>
        </ul>
      </li>
      <li parentName="ul"><inlineCode parentName="li">{`offersToBuy`}</inlineCode>{` — The offers to reserve in the `}<a parentName="li" {...{
          "href": "/pages-sales-guides#reserve-offer"
        }}>{`Sales API`}</a>{`, with quantity and possible traveller assignments.`}
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`selectableProductIds`}</inlineCode>{` — When populated, include these selectable IDs in the reservation request. They refer to optional products (found in the response under `}<inlineCode parentName="li">{`optionalProducts`}</inlineCode>{`) that are available with the recommended offers.`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`possibleTravellerIds`}</inlineCode>{` — Who can be assigned to each purchase. See `}<a parentName="li" {...{
              "href": "#traveller-assignment"
            }}>{`Traveller Assignment`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ul"><inlineCode parentName="li">{`combineWithToCoverEntireJourney`}</inlineCode>{` — When this recommendation doesn't cover the entire journey, other recommendations are provided that can be combined with this one to cover the full journey. Select `}<strong parentName="li">{`one`}</strong>{` item from the list, then purchase `}<strong parentName="li">{`all`}</strong>{` OffersToBuy from `}<strong parentName="li">{`all`}</strong>{` items in `}<inlineCode parentName="li">{`recommendationsToCoverEntireJourney`}</inlineCode>{`, together with the parent recommendation. `}<strong parentName="li">{`Only populated when `}<inlineCode parentName="strong">{`onlyIncludeMostCompleteInCategorySpec: true`}</inlineCode>{`.`}</strong>{` More details can be found in `}<a parentName="li" {...{
          "href": "#partial-journey-coverage"
        }}>{`Partial journey coverage`}</a>{`.`}</li>
    </ul>
    <p>{`To display a recommendation, look up each `}<inlineCode parentName="p">{`offersToBuy[].id`}</inlineCode>{` in the `}<inlineCode parentName="p">{`offers[]`}</inlineCode>{` array to get price and details.`}</p>
    <h2 {...{
      "id": "rulespec"
    }}>{`RuleSpec`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <p>{`The `}<inlineCode parentName="p">{`ruleSpec`}</inlineCode>{` controls how recommendations are calculated and filtered.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Setting`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Recommended`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`journeyOrganizeAlgorithm`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SUBSEQUENT_COMBINATIONS`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SUBSEQUENT_COMBINATIONS`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`How multi-leg journeys are split into recommendation groups.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`priceComparisonAlgorithm`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`BEFORE_SDR`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`TOTAL_PRICE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`How offers are compared when selecting the best offer.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`onlyIncludeRecommendedOffers`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Only return offers referenced by at least one recommendation.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`onlyIncludeRecommendationsWithOffersToBuy`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Omit recommendation categories with no matching offers. When true, every returned recommendation is guaranteed to have a non-empty `}<inlineCode parentName="td">{`offersToBuy`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`mixinOffersWithHigherFlexibility`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Allow higher-flexibility offers in lower categories if cheaper or the requested flexibility is unavailable on some legs. At least one offer in the recommendation must match the requested level. See `}<a parentName="td" {...{
              "href": "#mixing-flexibility-levels-mixinofferswithhigherflexibility"
            }}>{`example`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`mixInOffersWithOtherFacilitySets`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Allow mixing facility types to cover the full journey. For example, a SLEEPER recommendation may include a SEATING offer for legs where sleeper isn't available. At least one offer must match the requested facility. See `}<a parentName="td" {...{
              "href": "#mixing-facility-types-mixinofferswithotherfacilitysets"
            }}>{`example`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`removeSplitRailReplacementOffers`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`When a rail ServiceJourney has been partially replaced by alternative transport, recommendations will only be provided for the full ServiceJourney.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`onlyKeepUpgradeProductSalesPackages`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Only include upgrade products. Use when client has an entitlement (e.g., monthly pass) and searches for upgrades.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`onlyIncludeMostCompleteInCategorySpec`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`BETA:`}</strong>{` Filter out subset recommendations. See `}<a parentName="td" {...{
              "href": "#filtering-subset-recommendations-beta"
            }}>{`details below`}</a>{`.`}</td>
        </tr>
      </tbody>
    </table>
    <BannerAlertBox title="Why do defaults differ from recommended values?" variant="info" mdxType="BannerAlertBox">
    Several flags default to <code>false</code> because they were introduced incrementally — enabling new behaviour requires an explicit opt-in to avoid breaking existing integrations. In a future major version of the API, the recommended values will become the new defaults, and some flags may be removed entirely.
    </BannerAlertBox>
    <h3 {...{
      "id": "algorithm-details"
    }}>{`Algorithm Details`}</h3>
    <h4 {...{
      "id": "journey-organize-algorithms"
    }}>{`Journey Organize Algorithms`}</h4>
    <BannerAlertBox title="Recommendation" variant="info" mdxType="BannerAlertBox">
    Use <code>SUBSEQUENT_COMBINATIONS</code> — it enables full-journey recommendations for multi-operator journeys where no single offer covers all legs.
    </BannerAlertBox>
    <p>{`The algorithm controlling how recommendations should be combined spanning multiple legs.`}</p>
    <p>{`Example: For a journey with three legs (SJ1 → SJ2 → SJ3):`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Algorithm`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Recommendation groups created`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SUBSEQUENT_COMBINATIONS`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[SJ1]`}{`, `}{`[SJ2]`}{`, `}{`[SJ3]`}{`, `}{`[SJ1, SJ2]`}{`, `}{`[SJ2, SJ3]`}{`, `}{`[SJ1, SJ2, SJ3]`}{` (not `}{`[SJ1, SJ3]`}{`)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`FOR_EACH_AND_GROUPED_COMBINATIONS`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[SJ1]`}{`, `}{`[SJ2]`}{`, `}{`[SJ3]`}{`, `}{`[SJ1, SJ2, SJ3]`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`COMBINATIONS_FROM_OFFERS`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Only combinations matching actual offers`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`The choice of algorithm matters most on multi-operator journeys. Usually, there exists no tickets covering all operators, hence no single offer covers the full journey.`}</p>
    <p>{`Consider a two-leg journey: A → B with Operator X, then B → C with Operator Y.`}</p>
    <p>{`With `}<inlineCode parentName="p">{`COMBINATIONS_FROM_OFFERS`}</inlineCode>{`, the API only creates groups matching existing offers — one per leg, but no full-journey group:`}</p>
    <ExpandablePanel title="COMBINATIONS_FROM_OFFERS result" mdxType="ExpandablePanel">
      <pre>{`{
  "recommendations": [
    {
      "typeOfRecommendation": "CHEAPEST",
      "geographicalValidityCovered": {
        "coversEntireJourney": false,
        "serviceJourneys": ["operator-x-sj"]
      },
      "offersToBuy": [{ "id": "offer-a", "numberToBuy": 1 }]
    },
    {
      "typeOfRecommendation": "CHEAPEST",
      "geographicalValidityCovered": {
        "coversEntireJourney": false,
        "serviceJourneys": ["operator-y-sj"]
      },
      "offersToBuy": [{ "id": "offer-b", "numberToBuy": 1 }]
    }
  ]
}`}</pre>
    </ExpandablePanel>
    <p>{`The client receives two separate CHEAPEST recommendations but must figure out on its own that buying both covers the full journey. With `}<inlineCode parentName="p">{`onlyIncludeMostCompleteInCategorySpec`}</inlineCode>{` (BETA), each recommendation gets `}<a parentName="p" {...{
        "href": "#partial-journey-coverage"
      }}><inlineCode parentName="a">{`combineWithToCoverEntireJourney`}</inlineCode></a>{` links pointing to the other — but the client still does the composition.`}</p>
    <p>{`With `}<inlineCode parentName="p">{`SUBSEQUENT_COMBINATIONS`}</inlineCode>{`, the API also creates a group for the full journey and fills it with the cheapest combination of offers:`}</p>
    <ExpandablePanel title="SUBSEQUENT_COMBINATIONS result" mdxType="ExpandablePanel">
      <pre>{`{
  "recommendations": [
    {
      "typeOfRecommendation": "CHEAPEST",
      "geographicalValidityCovered": {
        "coversEntireJourney": false,
        "serviceJourneys": ["operator-x-sj"]
      },
      "offersToBuy": [{ "id": "offer-a", "numberToBuy": 1 }]
    },
    {
      "typeOfRecommendation": "CHEAPEST",
      "geographicalValidityCovered": {
        "coversEntireJourney": false,
        "serviceJourneys": ["operator-y-sj"]
      },
      "offersToBuy": [{ "id": "offer-b", "numberToBuy": 1 }]
    },
    {
      "typeOfRecommendation": "CHEAPEST",
      "geographicalValidityCovered": {
        "coversEntireJourney": true,
        "serviceJourneys": ["operator-x-sj", "operator-y-sj"]
      },
      "offersToBuy": [
        { "id": "offer-a", "numberToBuy": 1 },
        { "id": "offer-b", "numberToBuy": 1 }
      ]
    }
  ]
}`}</pre>
    </ExpandablePanel>
    <p>{`The third recommendation has `}<inlineCode parentName="p">{`coversEntireJourney: true`}</inlineCode>{` and lists both offers — the client gets a ready-made full-journey recommendation without combining offers itself.`}</p>
    <p><strong parentName="p">{`Key behaviours:`}</strong></p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`SUBSEQUENT_COMBINATIONS`}</inlineCode>{` only creates `}<em parentName="li">{`consecutive`}</em>{` leg groups — non-adjacent combinations like `}{`[SJ1, SJ3]`}{` are never generated`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`FOR_EACH_AND_GROUPED_COMBINATIONS`}</inlineCode>{` may miss optimal price combinations (no intermediate groups like `}{`[SJ1, SJ2]`}{`)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`COMBINATIONS_FROM_OFFERS`}</inlineCode>{` only creates groups matching actual product scopes — no full-journey group is created unless a single product covers it. This is useful when your client handles journey composition itself. Combined with `}<inlineCode parentName="li">{`onlyIncludeMostCompleteInCategorySpec`}</inlineCode>{` (BETA), partial recommendations get `}<a parentName="li" {...{
          "href": "#partial-journey-coverage"
        }}><inlineCode parentName="a">{`combineWithToCoverEntireJourney`}</inlineCode></a>{` links that help clients find complementary offers`}</li>
    </ul>
    <h4 {...{
      "id": "price-comparison-algorithms"
    }}>{`Price Comparison Algorithms`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Algorithm`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Compares`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Use case`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`TOTAL_PRICE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`What the user actually pays (after discounts)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Recommended for most use cases`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`BEFORE_SDR`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Original price before discounts`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Employee tickets (`}<em parentName="td">{`personalbillett`}</em>{`)`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`For both algorithms, the price comparison is done `}<strong parentName="p">{`per traveller`}</strong>{`.`}</p>
    <p><inlineCode parentName="p">{`BEFORE_SDR`}</inlineCode>{` is useful for employee tickets where all offers cost the same after discount. It selects the cheapest original product for a lower tax basis.`}</p>
    <p><strong parentName="p">{`Example:`}</strong>{` A customer with an employee ticket searches Oslo → Bergen.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Offer`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Original price`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`After discount`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Offer A`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`kr 200`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`kr 0`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Offer B`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`kr 100`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`kr 0`}</td>
        </tr>
      </tbody>
    </table>
    <ul>
      <li parentName="ul"><strong parentName="li"><inlineCode parentName="strong">{`TOTAL_PRICE`}</inlineCode></strong>{`: Both are kr 0 — tie, so selection is arbitrary`}</li>
      <li parentName="ul"><strong parentName="li"><inlineCode parentName="strong">{`BEFORE_SDR`}</inlineCode></strong>{`: Selects Offer B (kr 100 < kr 200) — lower tax basis`}</li>
    </ul>
    <h4 {...{
      "id": "filtering-subset-recommendations-beta"
    }}>{`Filtering Subset Recommendations (BETA)`}</h4>
    <BannerAlertBox title="BETA" variant="warning" mdxType="BannerAlertBox">
    This feature may change or be removed in future versions.
    </BannerAlertBox>
    <p>{`When `}<inlineCode parentName="p">{`onlyIncludeMostCompleteInCategorySpec: true`}</inlineCode>{`, the API filters out recommendations that are strict subsets of other recommendations within the same category specification.`}</p>
    <p>{`For a multi-leg journey with recommendations for `}{`[A]`}{`, `}{`[B]`}{`, and `}{`[A, B]`}{` (all with identical `}<inlineCode parentName="p">{`typeOfRecommendation`}</inlineCode>{`, `}<inlineCode parentName="p">{`durationType`}</inlineCode>{`, `}<inlineCode parentName="p">{`fareClass`}</inlineCode>{`, and `}<inlineCode parentName="p">{`facilitySet`}</inlineCode>{`), only the recommendation for `}{`[A, B]`}{` is returned.`}</p>
    <p><strong parentName="p">{`Key behaviours:`}</strong></p>
    <ul>
      <li parentName="ul">{`Only applies to multi-leg journeys; single-leg journeys are unaffected`}</li>
      <li parentName="ul">{`Subset filtering only considers recommendations with `}<inlineCode parentName="li">{`offersToBuy`}</inlineCode></li>
      <li parentName="ul">{`Recommendations without offers are preserved when `}<inlineCode parentName="li">{`onlyIncludeRecommendationsWithOffersToBuy: false`}</inlineCode></li>
      <li parentName="ul"><strong parentName="li">{`Enables `}<inlineCode parentName="strong">{`combineWithToCoverEntireJourney`}</inlineCode></strong>{` on incomplete recommendations to help clients find complementary offers (see `}<a parentName="li" {...{
          "href": "#partial-journey-coverage"
        }}>{`Partial journey coverage`}</a>{`)`}</li>
    </ul>
    <h2 {...{
      "id": "categoryspec"
    }}>{`CategorySpec`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <p>{`The `}<inlineCode parentName="p">{`categorySpec`}</inlineCode>{` defines which types of recommendations to generate.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Required`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Values`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`typesOfRecommendation`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Yes`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`FLEXIBLE`}</inlineCode>{`, `}<inlineCode parentName="td">{`SEMI_FLEXIBLE`}</inlineCode>{`, `}<inlineCode parentName="td">{`NON_FLEXIBLE`}</inlineCode>{`, `}<inlineCode parentName="td">{`CHEAPEST`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`durationTypes`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SINGLE_TRIP`}</inlineCode>{`, `}<inlineCode parentName="td">{`RETURN_TRIP`}</inlineCode>{`, `}<inlineCode parentName="td">{`CARNET`}</inlineCode>{`, `}<inlineCode parentName="td">{`DAY_PASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`WEEKLY_PASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`WEEKEND_PASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`MONTHLY_PASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`SEASON_TICKET`}</inlineCode>{`, `}<inlineCode parentName="td">{`PROFILE_MEMBERSHIP`}</inlineCode>{`, `}<inlineCode parentName="td">{`OPEN_ENDED`}</inlineCode>{`, `}<inlineCode parentName="td">{`OTHER`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fareClasses`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`FIRST_CLASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`SECOND_CLASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`THIRD_CLASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`ECONOMY_CLASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`BUSINESS_CLASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`TURISTA`}</inlineCode>{`, `}<inlineCode parentName="td">{`PREFERENTE`}</inlineCode>{`, `}<inlineCode parentName="td">{`PREMIUM_CLASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`ANY`}</inlineCode>{`, `}<inlineCode parentName="td">{`STANDARD_CLASS`}</inlineCode>{`, `}<inlineCode parentName="td">{`UNKNOWN`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`facilitySet`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SLEEPER`}</inlineCode>{`, `}<inlineCode parentName="td">{`SEATING`}</inlineCode>{`, `}<inlineCode parentName="td">{`COUCHETTE`}</inlineCode>{`, `}<inlineCode parentName="td">{`RECLINING_SEAT`}</inlineCode>{`, `}<inlineCode parentName="td">{`ANY_FACILITY_SET`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p>{`When a field is omitted from `}<inlineCode parentName="p">{`categorySpec`}</inlineCode>{`, it acts as an implicit wildcard — recommendations will include all offers regardless of that attribute. You can also use explicit wildcard values (`}<inlineCode parentName="p">{`ANY`}</inlineCode>{`, `}<inlineCode parentName="p">{`ANY_FACILITY_SET`}</inlineCode>{`) to include a wildcard alongside specific values.`}</p>
    <p>{`For example, requesting `}<inlineCode parentName="p">{`fareClasses: ["STANDARD_CLASS", "ANY"]`}</inlineCode>{` generates separate recommendations for STANDARD_CLASS offers `}<em parentName="p">{`and`}</em>{` for the cheapest across all fare classes. `}<inlineCode parentName="p">{`typesOfRecommendation`}</inlineCode>{` is the only required field and must contain at least one value.`}</p>
    <p>{`These two requests are functionally equivalent — omitting a field has the same effect as specifying its wildcard value:`}</p>
    <ExpandablePanel title="Implicit vs explicit wildcards" mdxType="ExpandablePanel">
      <pre>{`// Minimal — omitted fields act as implicit wildcards
{
  "recommendationConfig": {
    "categorySpec": {
      "typesOfRecommendation": ["CHEAPEST"]
    }
  }
}
// Equivalent — explicit wildcard values
{
  "recommendationConfig": {
    "categorySpec": {
      "typesOfRecommendation": ["CHEAPEST"],
      "fareClasses": ["ANY"],
      "facilitySet": ["ANY_FACILITY_SET"]
    }
  }
}`}</pre>
    </ExpandablePanel>
    <h3 {...{
      "id": "flexibility-levels"
    }}>{`Flexibility Levels`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Level`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Refundable`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Exchangeable`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CHEAPEST`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`—`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`—`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Lowest price regardless of flexibility`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`NON_FLEXIBLE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Cannot be refunded or exchanged`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SEMI_FLEXIBLE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Either`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Either`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Either refundable or exchangeable, but not both`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`FLEXIBLE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Yes`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Yes`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Both refundable and exchangeable`}</td>
        </tr>
      </tbody>
    </table>
    <BannerAlertBox title="Mixing flexibility levels" variant="info" mdxType="BannerAlertBox">
    When <code>mixinOffersWithHigherFlexibility: true</code>, a lower-flexibility category may include offers with higher flexibility if they are cheaper or the requested flexibility is unavailable. At least one offer must match the requested level. See <a href="#rulespec">RuleSpec</a> for details and <a href="#mixing-flexibility-levels-mixinofferswithhigherflexibility">Examples</a> for an illustration.
    </BannerAlertBox>
    <h2 {...{
      "id": "traveller-assignment"
    }}>{`Traveller Assignment`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <p>{`The `}<inlineCode parentName="p">{`possibleTravellerIds`}</inlineCode>{` field in `}<inlineCode parentName="p">{`offersToBuy`}</inlineCode>{` tells you who to assign to each purchase. It is a list of lists — each inner list represents one purchase, and the number of inner lists always equals `}<inlineCode parentName="p">{`numberToBuy`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Individual tickets`}</strong>{` — each inner list contains one traveller:`}</p>
    <ExpandablePanel title="Individual tickets example" mdxType="ExpandablePanel">
      <pre>{`"offersToBuy": [{
  "id": "offer-a",
  "numberToBuy": 2,
  "possibleTravellerIds": [["traveller-1"], ["traveller-2"]]
}]`}</pre>
    </ExpandablePanel>
    <p>{`Buy offer-a twice: once for traveller-1, once for traveller-2.`}</p>
    <p><strong parentName="p">{`Group tickets`}</strong>{` — the inner list contains all travellers sharing one ticket (e.g. a sleeper compartment):`}</p>
    <ExpandablePanel title="Group ticket example" mdxType="ExpandablePanel">
      <pre>{`"offersToBuy": [{
  "id": "offer-b",
  "numberToBuy": 1,
  "possibleTravellerIds": [["traveller-1", "traveller-2", "traveller-3", "traveller-4"]]
}]`}</pre>
    </ExpandablePanel>
    <p>{`Buy offer-b once for all four travellers together. The underlying product may have per-user-type constraints (e.g. minimum 1 adult, up to 3 children) — the recommendation system has already solved the assignment for you.`}</p>
    <p><strong parentName="p">{`Mixed`}</strong>{` — the system may combine individual and group tickets when that is cheapest:`}</p>
    <ExpandablePanel title="Mixed example" mdxType="ExpandablePanel">
      <pre>{`"offersToBuy": [
  {
    "id": "offer-c",
    "numberToBuy": 1,
    "possibleTravellerIds": [["traveller-1"]]
  },
  {
    "id": "offer-d",
    "numberToBuy": 1,
    "possibleTravellerIds": [["traveller-2", "traveller-3", "traveller-4", "traveller-5"]]
  }
]`}</pre>
    </ExpandablePanel>
    <p><strong parentName="p">{`Summary:`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`possibleTravellerIds`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`numberToBuy`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Meaning`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`[["A"], ["B"]]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Two individual purchases`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`[["A", "B"]]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`One group purchase`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`[["A"], ["B", "C", "D"]]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`One individual + one group`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`You do not need to inspect the offer's `}<inlineCode parentName="p">{`travellerMapping`}</inlineCode>{` to use the recommendation — `}<inlineCode parentName="p">{`possibleTravellerIds`}</inlineCode>{` is the ready-made grouping.`}</p>
    <h2 {...{
      "id": "examples"
    }}>{`Examples`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <h3 {...{
      "id": "how-categoryspec-dimensions-combine"
    }}>{`How categorySpec dimensions combine`}</h3>
    <p>{`Each categorySpec dimension multiplies the number of recommendation slots. The API generates one recommendation per unique combination and filters out empty slots (when `}<inlineCode parentName="p">{`onlyIncludeRecommendationsWithOffersToBuy: true`}</inlineCode>{`). Note that the same offer can appear in multiple recommendations — for example, the cheapest NON_FLEXIBLE offer may also be the overall CHEAPEST.`}</p>
    <p><strong parentName="p">{`Example offers:`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Id`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Price`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Flexibility`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`DurationType`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`FacilitySet`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleSeating`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`10`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SINGLE_TRIP`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEATING`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleCouchette`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`20`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SINGLE_TRIP`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`COUCHETTE`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SemiFlexibleSeating`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`30`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEMI_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SINGLE_TRIP`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEATING`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SemiFlexibleSleeper`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`40`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEMI_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SINGLE_TRIP`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SLEEPER`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`FlexibleCouchette`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`50`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SINGLE_TRIP`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`COUCHETTE`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`FlexibleSleeper`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`60`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SINGLE_TRIP`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SLEEPER`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`WeekPass`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`70`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`WEEKLY_PASS`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`—`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`MonthPass`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`80`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MONTHLY_PASS`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`—`}</td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`One dimension — flexibility only:`}</strong></p>
    <pre><code parentName="pre" {...{}}>{`typesOfRecommendation: [CHEAPEST, NON_FLEXIBLE, SEMI_FLEXIBLE, FLEXIBLE]
`}</code></pre>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`typeOfRecommendation`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Offer recommended`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CHEAPEST`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleSeating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleSeating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SEMI_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SemiFlexibleSeating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`FlexibleCouchette`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`4 slots requested, 4 filled. Each picks the cheapest offer matching that flexibility level.`}</p>
    <p><strong parentName="p">{`Two dimensions — flexibility × facilitySet:`}</strong></p>
    <pre><code parentName="pre" {...{}}>{`typesOfRecommendation: [CHEAPEST, NON_FLEXIBLE, SEMI_FLEXIBLE, FLEXIBLE]
facilitySet: [ANY_FACILITY_SET, SEATING, COUCHETTE, SLEEPER]
`}</code></pre>
    <p>{`This creates 4 × 4 = 16 possible slots. Only 13 have matching offers:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`typeOfRecommendation`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`facilitySet`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Offer recommended`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CHEAPEST`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ANY_FACILITY_SET`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleSeating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CHEAPEST`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEATING`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleSeating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CHEAPEST`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`COUCHETTE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleCouchette`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CHEAPEST`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SLEEPER`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SemiFlexibleSleeper`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ANY_FACILITY_SET`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleSeating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEATING`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleSeating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`COUCHETTE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NonFlexibleCouchette`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SEMI_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ANY_FACILITY_SET`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SemiFlexibleSeating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SEMI_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEATING`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SemiFlexibleSeating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SEMI_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SLEEPER`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SemiFlexibleSleeper`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ANY_FACILITY_SET`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`FlexibleCouchette`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`COUCHETTE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`FlexibleCouchette`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SLEEPER`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`FlexibleSleeper`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Three combinations have no matching offers and are omitted: NON_FLEXIBLE + SLEEPER, SEMI_FLEXIBLE + COUCHETTE, and FLEXIBLE + SEATING. With `}<inlineCode parentName="p">{`onlyIncludeRecommendationsWithOffersToBuy: false`}</inlineCode>{`, these would appear as empty recommendations.`}</p>
    <p><strong parentName="p">{`Adding a third dimension`}</strong>{` (e.g., `}<inlineCode parentName="p">{`durationTypes: [SINGLE_TRIP, WEEKLY_PASS, MONTHLY_PASS]`}</inlineCode>{`) would multiply the slots further: 4 × 4 × 3 = 48 possible slots, with only the non-empty ones returned.`}</p>
    <h3 {...{
      "id": "mixing-flexibility-levels-mixinofferswithhigherflexibility"
    }}>{`Mixing flexibility levels (`}<inlineCode parentName="h3">{`mixinOffersWithHigherFlexibility`}</inlineCode>{`)`}</h3>
    <p><strong parentName="p">{`Scenario:`}</strong>{` User searches Oslo → Bodø with `}<inlineCode parentName="p">{`typesOfRecommendation: ["NON_FLEXIBLE"]`}</inlineCode>{`, but a FLEXIBLE offer is cheaper than the NON_FLEXIBLE alternative on one leg.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Offer`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Flexibility`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Price`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Leg`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Offer A`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`kr 200`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Oslo → Trondheim`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Offer B`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`kr 150`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Oslo → Trondheim`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Offer C`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NON_FLEXIBLE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`kr 300`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Trondheim → Bodø`}</td>
        </tr>
      </tbody>
    </table>
    <ExpandablePanel title="With mixinOffersWithHigherFlexibility: false" mdxType="ExpandablePanel">
      <pre>{`{
  "recommendations": [
    {
      "typeOfRecommendation": "NON_FLEXIBLE",
      "offersToBuy": [
        { "id": "offer-a", "numberToBuy": 1 },
        { "id": "offer-c", "numberToBuy": 1 }
      ]
    }
  ]
}`}</pre>
    </ExpandablePanel>
    <p>{`Result: NON_FLEXIBLE uses Offer A (kr 200) + Offer C (kr 300) = `}<strong parentName="p">{`kr 500`}</strong>{`. Only NON_FLEXIBLE offers are considered.`}</p>
    <ExpandablePanel title="With mixinOffersWithHigherFlexibility: true" mdxType="ExpandablePanel">
      <pre>{`{
  "recommendations": [
    {
      "typeOfRecommendation": "NON_FLEXIBLE",
      "offersToBuy": [
        { "id": "offer-b", "numberToBuy": 1 },
        { "id": "offer-c", "numberToBuy": 1 }
      ]
    }
  ]
}`}</pre>
    </ExpandablePanel>
    <p>{`Result: NON_FLEXIBLE uses the cheaper FLEXIBLE Offer B (kr 150) for leg 1 + NON_FLEXIBLE Offer C (kr 300) for leg 2 = `}<strong parentName="p">{`kr 450`}</strong>{`. The user saves kr 50 and gets higher flexibility on one leg.`}</p>
    <p><strong parentName="p">{`Key behaviours:`}</strong></p>
    <ul>
      <li parentName="ul">{`Flexibility hierarchy (low → high): `}<inlineCode parentName="li">{`NON_FLEXIBLE`}</inlineCode>{` → `}<inlineCode parentName="li">{`SEMI_FLEXIBLE`}</inlineCode>{` → `}<inlineCode parentName="li">{`FLEXIBLE`}</inlineCode></li>
      <li parentName="ul">{`Higher-flexibility offers can replace lower on individual legs, but not vice versa`}</li>
      <li parentName="ul">{`At least one offer in `}<inlineCode parentName="li">{`offersToBuy`}</inlineCode>{` must match the requested flexibility level — if no NON_FLEXIBLE offers exist for any leg, no NON_FLEXIBLE recommendation is created. This prevents recommendations labelled `}<inlineCode parentName="li">{`NON_FLEXIBLE`}</inlineCode>{` that contain only flexible offers`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`CHEAPEST`}</inlineCode>{` category is not affected by this flag`}</li>
    </ul>
    <h3 {...{
      "id": "mixing-facility-types-mixinofferswithotherfacilitysets"
    }}>{`Mixing facility types (`}<inlineCode parentName="h3">{`mixInOffersWithOtherFacilitySets`}</inlineCode>{`)`}</h3>
    <p><strong parentName="p">{`Scenario:`}</strong>{` User searches Oslo → Bodø with `}<inlineCode parentName="p">{`facilitySet: ["SLEEPER"]`}</inlineCode>{`, but sleeper is only available for part of the journey.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Offer`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Facility`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Leg`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Offer A`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SLEEPER`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Oslo → Trondheim`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Offer B`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEATING`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Trondheim → Bodø`}</td>
        </tr>
      </tbody>
    </table>
    <ExpandablePanel title="With mixInOffersWithOtherFacilitySets: false" mdxType="ExpandablePanel">
      <pre>{`{
  "recommendations": []
}`}</pre>
    </ExpandablePanel>
    <p>{`Result: The SLEEPER category is empty because sleeper alone can't cover the full journey.`}</p>
    <ExpandablePanel title="With mixInOffersWithOtherFacilitySets: true" mdxType="ExpandablePanel">
      <pre>{`{
  "recommendations": [
    {
      "facilitySet": "SLEEPER",
      "offersToBuy": [
        { "id": "offer-a" },
        { "id": "offer-b" }
      ]
    }
  ]
}`}</pre>
    </ExpandablePanel>
    <p>{`Result: SLEEPER recommendation combines sleeper (where available) with seating (where necessary).`}</p>
    <p><strong parentName="p">{`Key behaviours:`}</strong></p>
    <ul>
      <li parentName="ul">{`The algorithm tries the requested facility first, only mixing in others when necessary`}</li>
      <li parentName="ul">{`Recommendations consisting `}<em parentName="li">{`entirely`}</em>{` of non-requested facilities are never created`}</li>
      <li parentName="ul">{`All other dimensions are preserved. For instance, a SEMI_FLEXIBLE recommendation will only look for offers also matching this dimension`}</li>
    </ul>
    <h3 {...{
      "id": "duration-types"
    }}>{`Duration types`}</h3>
    <p><strong parentName="p">{`Scenario:`}</strong>{` Products are available as single trips, day passes, monthly passes and more. You want to present the cheapest option per duration type.`}</p>
    <p><strong parentName="p">{`categorySpec:`}</strong></p>
    <pre><code parentName="pre" {...{}}>{`typesOfRecommendation: [CHEAPEST]
durationTypes: [SINGLE_TRIP, DAY_PASS, MONTHLY_PASS]
`}</code></pre>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`typeOfRecommendation`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`durationType`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Offer recommended`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CHEAPEST`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SINGLE_TRIP`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Single ticket`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CHEAPEST`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DAY_PASS`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Day pass`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CHEAPEST`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MONTHLY_PASS`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Monthly pass`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`The API returns one recommendation per duration type, each pointing to the cheapest offer of that duration. This lets clients present options like "Single trip: kr 39", "Day pass: kr 109", "Monthly pass: kr 790" side by side.`}</p>
    <p>{`You can combine `}<inlineCode parentName="p">{`durationTypes`}</inlineCode>{` with other categorySpec dimensions. For example, adding `}<inlineCode parentName="p">{`typesOfRecommendation: [CHEAPEST, NON_FLEXIBLE, FLEXIBLE]`}</inlineCode>{` generates up to 3 × 3 = 9 recommendations (one per flexibility level per duration type). Empty combinations (where no matching offers exist) are filtered out when `}<inlineCode parentName="p">{`onlyIncludeRecommendationsWithOffersToBuy: true`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "rail-replacement-filtering"
    }}>{`Rail replacement filtering`}</h3>
    <p><strong parentName="p">{`Scenario:`}</strong>{` Service journey `}<inlineCode parentName="p">{`A`}</inlineCode>{` is partially replaced by bus (`}<inlineCode parentName="p">{`BFT A`}</inlineCode>{`), followed by another rail leg `}<inlineCode parentName="p">{`B`}</inlineCode>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Leg`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Note`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`A`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Rail`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Remaining rail portion of original SJ`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`BFT A`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Rail replacement`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Bus replacing part of A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`B`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Rail`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Separate service journey`}</td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`With `}<inlineCode parentName="strong">{`removeSplitRailReplacementOffers: false`}</inlineCode>{`:`}</strong></p>
    <pre><code parentName="pre" {...{}}>{`Recommendations for: [A], [BFT A], [B], [A, BFT A], [BFT A, B], [A, BFT A, B]
`}</code></pre>
    <p><strong parentName="p">{`With `}<inlineCode parentName="strong">{`removeSplitRailReplacementOffers: true`}</inlineCode>{`:`}</strong></p>
    <pre><code parentName="pre" {...{}}>{`Recommendations for: [A], [B], [A, BFT A], [A, BFT A, B]
`}</code></pre>
    <p>{`Both `}<inlineCode parentName="p">{`[BFT A]`}</inlineCode>{` and `}<inlineCode parentName="p">{`[BFT A, B]`}</inlineCode>{` are removed because they contain the rail-replacement leg without its corresponding rail leg (`}<inlineCode parentName="p">{`A`}</inlineCode>{`).`}</p>
    <p><strong parentName="p">{`Key behaviour:`}</strong>{` When a service journey is `}<em parentName="p">{`partially`}</em>{` replaced by bus (i.e., both rail and rail-replacement portions exist), any recommendation group that contains a rail-replacement leg without its corresponding rail leg is removed. If the entire service journey is replaced by bus, no filtering occurs.`}</p>
    <h3 {...{
      "id": "partial-journey-coverage"
    }}>{`Partial journey coverage`}</h3>
    <BannerAlertBox title="Requires" variant="info" mdxType="BannerAlertBox">
    <code>onlyIncludeMostCompleteInCategorySpec: true</code> in <code>ruleSpec</code> (BETA feature)
    </BannerAlertBox>
    <p>{`When a recommendation doesn't cover the entire journey (`}<inlineCode parentName="p">{`coversEntireJourney: false`}</inlineCode>{`), the `}<inlineCode parentName="p">{`combineWithToCoverEntireJourney`}</inlineCode>{` field lists other recommendations that can be combined to cover the full journey.`}</p>
    <p><strong parentName="p">{`Scenario:`}</strong>{` A two-leg journey with two operators. No single ticket covers both legs.`}</p>
    <ExpandablePanel title="Example response with combineWithToCoverEntireJourney" mdxType="ExpandablePanel">
      <pre>{`{
  "recommendations": [
    {
      "geographicalValidityCovered": {
        "serviceJourneys": ["operator-x-sj"],
        "pointToPointValidity": {
          "fromPlace": "stop-place-a",
          "toPlace": "stop-place-b"
        },
        "coversEntireJourney": false
      },
      "typeOfRecommendation": "FLEXIBLE",
      "durationType": "SINGLE_TRIP",
      "fareClass": "STANDARD_CLASS",
      "facilitySet": "ANY_FACILITY_SET",
      "offersToBuy": [
        {
          "id": "offer-1",
          "numberToBuy": 1,
          "possibleTravellerIds": [["traveller-1"]]
        }
      ],
      "combineWithToCoverEntireJourney": [
        {
          "geographicalValidityCovered": {
            "serviceJourneys": ["operator-y-sj"],
            "coversEntireJourney": false
          },
          "recommendationsToCoverEntireJourney": [
            {
              "geographicalValidityCovered": {
                "serviceJourneys": ["operator-y-sj"],
                "zonalValidity": {
                  "fareZones": ["fare-zone-1"]
                },
                "coversEntireJourney": false
              },
              "typeOfRecommendation": "NON_FLEXIBLE",
              "fareClass": "STANDARD_CLASS",
              "facilitySet": "ANY_FACILITY_SET",
              "offersToBuy": [
                {
                  "id": "offer-2",
                  "numberToBuy": 1,
                  "possibleTravellerIds": [["traveller-1"]]
                }
              ]
            }
          ]
        }
      ]
    }
  ]
}`}</pre>
    </ExpandablePanel>
    <p><strong parentName="p">{`How to use:`}</strong></p>
    <ol>
      <li parentName="ol">{`Check if `}<inlineCode parentName="li">{`coversEntireJourney`}</inlineCode>{` is `}<inlineCode parentName="li">{`false`}</inlineCode></li>
      <li parentName="ol">{`If so, look at `}<inlineCode parentName="li">{`combineWithToCoverEntireJourney`}</inlineCode>{` for complementary recommendations`}</li>
      <li parentName="ol">{`Select `}<strong parentName="li">{`one`}</strong>{` item from the list — each item covers a specific geographical gap`}</li>
      <li parentName="ol">{`Purchase offers from `}<strong parentName="li">{`all`}</strong>{` recommendations in the selected item's `}<inlineCode parentName="li">{`recommendationsToCoverEntireJourney`}</inlineCode>{`, together with the parent recommendation, to cover the full journey`}</li>
    </ol>
    <h2 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <h3 {...{
      "id": "unexpected-offers-in-recommendations"
    }}>{`Unexpected offers in recommendations`}</h3>
    <ol>
      <li parentName="ol">{`Check if `}<inlineCode parentName="li">{`mixinOffersWithHigherFlexibility`}</inlineCode>{` is enabled. This may place FLEXIBLE offers in NON_FLEXIBLE categories`}</li>
      <li parentName="ol">{`Check if `}<inlineCode parentName="li">{`mixInOffersWithOtherFacilitySets`}</inlineCode>{` is enabled. This may mix SEATING into SLEEPER categories`}</li>
      <li parentName="ol">{`Verify `}<inlineCode parentName="li">{`priceComparisonAlgorithm`}</inlineCode>{`—`}<inlineCode parentName="li">{`BEFORE_SDR`}</inlineCode>{` may select different offers than `}<inlineCode parentName="li">{`TOTAL_PRICE`}</inlineCode></li>
    </ol>
    <h3 {...{
      "id": "missing-flexibility-levels"
    }}>{`Missing flexibility levels`}</h3>
    <p>{`Not all products support all flexibility options. If no FLEXIBLE offers exist for a route, no FLEXIBLE recommendation is returned.`}</p>
    <h3 {...{
      "id": "non_flexible-recommendation-contains-a-mix-of-flexible-and-non-flexible-offers"
    }}>{`NON_FLEXIBLE recommendation contains a mix of flexible and non-flexible offers`}</h3>
    <p>{`This happens when `}<inlineCode parentName="p">{`mixinOffersWithHigherFlexibility: true`}</inlineCode>{` and a flexible offer is cheaper on one or more legs. This is expected behaviour; the system found a better deal by using a higher-flexibility offer where it's cheaper, while keeping at least one non-flexible offer in the recommendation. See `}<a parentName="p" {...{
        "href": "#mixing-flexibility-levels-mixinofferswithhigherflexibility"
      }}>{`example`}</a>{`.`}</p>
    <h3 {...{
      "id": "large-response-size"
    }}>{`Large response size`}</h3>
    <p>{`Enable `}<inlineCode parentName="p">{`onlyIncludeRecommendedOffers: true`}</inlineCode>{` to exclude offers not part of any recommendation.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      